// Centralized helpers for URL-driven search on the Transactions page

const toLower = (s) => String(s || "").toLowerCase().trim();

// split "source → dest" or "source -> dest" (many arrow variants)
function parseAccountPair(raw) {
  const s = toLower(raw);
  if (!s) return null;
  // include common ASCII and Unicode right-arrow-ish separators
  const ARROW = /\s*(?:->|→|⇒|⟶|—>|–>|➝|➜|➔|→)\s*/;
  const parts = s.split(ARROW);
  if (parts.length === 2) {
    const left = parts[0].trim();
    const right = parts[1].trim();
    if (left && right) return { left, right };
  }
  return null;
}

function getTagArray(row) {
  if (Array.isArray(row?.tagPath)) return row.tagPath;
  if (Array.isArray(row?.tagPaths)) return row.tagPaths; // legacy
  return [];
}

/**
 * Returns { q, account, tag, status, period, isActive }
 * All values are raw (unlowercased) except isActive (boolean).
 */
export function getSearchParamsFromLocation(search = "") {
  const sp = new URLSearchParams(search || "");
  const obj = {
    q: sp.get("q") || "",
    account: sp.get("account") || "",
    tag: sp.get("tag") || "",
    status: sp.get("status") || "",
    period: sp.get("period") || "",
  };
  const isActive = !!(obj.q || obj.account || obj.tag || obj.status || obj.period);
  return { ...obj, isActive };
}

/**
 * q: matches description OR account name OR any tag path (case-insensitive contains)
 * account: contains on accountName/account. Also supports pair queries:
 *          "Source → Destination" (or "Source -> Destination") which match
 *          transfer groups where OUT contains Source and IN contains Destination.
 * tag: contains on any tagPath entry (hierarchical "any level")
 * status: contains on status
 * period: "YYYY" or "YYYY-MM"
 */
export function filterTransactions(
  rows,
  { q = "", account = "", tag = "", status = "", period = "" } = {}
) {
  const qL = toLower(q);
  const accL = toLower(account);
  const tagL = toLower(tag);
  const stL = toLower(status);
  const per = String(period || "").trim();

  if (!rows?.length) return rows || [];

  // ---------- Pair-aware transfer search precomputation ----------
  // If "account" looks like "A → B", we will:
  //   1) Build transfer groups from all rows
  //   2) Keep ONLY groups whose OUT contains A and IN contains B
  //   3) Apply q/tag/status/period to either leg (group passes if any leg passes)
  const pair = parseAccountPair(accL);
  let pairGroupIdsToKeep = null;

  if (pair) {
    // Build transfer groups: gid -> { in, out }
    const groups = new Map();
    for (const r of rows) {
      const isTransfer = toLower(r?.typeName).includes("transfer") || !!r?.transferGroupId;
      const gid = r?.transferGroupId;
      if (!isTransfer || !gid) continue;
      const side = String(r?.transferSide || "").toUpperCase();
      const g = groups.get(gid) || { in: null, out: null };
      if (side === "IN") g.in = r;
      else if (side === "OUT") g.out = r;
      groups.set(gid, g);
    }

    // helper to apply non-account filters against a single row
    const matchesOtherFilters = (r) => {
      if (!r) return false;

      if (qL) {
        const desc = toLower(r?.description);
        const accName = toLower(r?.accountName || r?.account);
        const tags = getTagArray(r).map((t) => toLower(t));
        if (!(desc.includes(qL) || accName.includes(qL) || tags.some((t) => t.includes(qL)))) {
          return false;
        }
      }

      if (tagL) {
        const tags = getTagArray(r).map((t) => toLower(t));
        if (!tags.some((t) => t.includes(tagL))) return false;
      }

      if (stL) {
        const s = toLower(r?.status);
        if (!s.includes(stL)) return false;
      }

      if (per) {
        const d = String(r?.date || "");
        if (/^\d{4}-\d{2}$/.test(per)) {
          if (!d.startsWith(per + "-")) return false;
        } else if (/^\d{4}$/.test(per)) {
          if (!d.startsWith(per + "-")) return false;
        }
      }

      return true;
    };

    // find groups where OUT contains left and IN contains right
    // and at least one leg matches the other filters
    pairGroupIdsToKeep = new Set();
    for (const [gid, g] of groups.entries()) {
      if (!g.in || !g.out) continue;
      const outAcc = toLower(g.out?.accountName || g.out?.account);
      const inAcc = toLower(g.in?.accountName || g.in?.account);
      const accountMatch = outAcc.includes(pair.left) && inAcc.includes(pair.right);
      if (!accountMatch) continue;

      if (matchesOtherFilters(g.in) || matchesOtherFilters(g.out)) {
        pairGroupIdsToKeep.add(gid);
      }
    }
  }

  // ---------- Main row filter ----------
  return rows.filter((r) => {
    // In pair mode, we must NOT apply the per-row "q" filter up front,
    // otherwise one leg could be dropped and the merge would break.
    if (!pair && qL) {
      const desc = toLower(r?.description);
      const accName = toLower(r?.accountName || r?.account);
      const tags = getTagArray(r).map((t) => toLower(t));
      const qMatch =
        desc.includes(qL) || accName.includes(qL) || tags.some((t) => t.includes(qL));
      if (!qMatch) return false;
    }

    if (pair) {
      const gid = r?.transferGroupId;
      const isTransfer = toLower(r?.typeName).includes("transfer") || !!gid;
      if (!isTransfer || !gid) return false;
      if (!pairGroupIdsToKeep || !pairGroupIdsToKeep.has(gid)) return false;
    } else if (accL) {
      const accName = toLower(r?.accountName || r?.account);
      if (!accName.includes(accL)) return false;
    }

    if (tagL) {
      const tags = getTagArray(r).map((t) => toLower(t));
      if (!tags.some((t) => t.includes(tagL))) return false; // any level
    }

    if (stL) {
      const s = toLower(r?.status);
      if (!s.includes(stL)) return false;
    }

    if (per) {
      const d = String(r?.date || "");
      if (/^\d{4}-\d{2}$/.test(per)) {
        if (!d.startsWith(per + "-")) return false;
      } else if (/^\d{4}$/.test(per)) {
        if (!d.startsWith(per + "-")) return false;
      }
    }

    return true;
  });
}
